import { createSignal, type SimpleSignal } from "@revideo/core";
import { FPS, frameToMs } from "@hudhud/reel/math";

/**
 * The one clock every layer reads. The scene's driver sets it once per frame before the frame is drawn, so a property written
 * as `() => f(ctx.frame())` is evaluated with the frame Remotion's `useCurrentFrame()` would have given — the same maths, unchanged.
 */
export interface Clock {
  /** the composition frame being drawn */
  readonly frame: () => number;
  /** the composition time being drawn, ms */
  readonly ms: () => number;
  readonly fps: number;
  /** advances the clock; only the driver calls this */
  readonly set: (frame: number) => void;
}

export function makeClock(fps = FPS): Clock {
  const frame: SimpleSignal<number> = createSignal(0);
  return { frame: () => frame(), ms: () => frameToMs(frame()), fps, set: (f) => { frame(f); } };
}

/** A layer that starts `from` frames in and lasts `dur` frames: its own local frame, and whether it is on at all. */
export interface Span { from: number; dur: number }
export const localFrame = (clock: Clock, span: Span) => clock.frame() - span.from;
export const within = (clock: Clock, span: Span) => { const l = localFrame(clock, span); return l >= 0 && l < span.dur; };
/** 1 inside the span, 0 outside: the opacity of a layer that only exists for its own frames. */
export const onOff = (clock: Clock, span: Span) => (within(clock, span) ? 1 : 0);
